import ExcelJS from 'exceljs';

/**
 * Client-side XLSX download helper. Replaces the legacy in-browser CSV
 * blob path used by Inventario / Ventas / Tesorería export buttons. The
 * client requested real Excel files so users can open them natively
 * (no "import wizard"), sum numeric cells, and apply Excel filters.
 *
 * Mirrors the server helper at [server/src/lib/xlsx.ts] one-for-one so
 * both stay easy to reason about together.
 *
 * Esto arma una grilla pelada. Los dos reportes "presentables" (Inventario
 * valorizado y resumen de Tesorería) arman su propio workbook con
 * `@/shared/lib/xlsxStyle` y lo bajan con `downloadWorkbook`.
 */

export type XlsxCell = string | number | null | undefined;

export type XlsxTotal = { label: string; value: string | number };

export type DownloadXlsxArgs = {
  /** Without extension — the helper appends `.xlsx`. */
  filename: string;
  /** Defaults to 'Hoja1'. */
  sheetName?: string;
  headers: string[];
  rows: Array<Array<XlsxCell>>;
  totals?: XlsxTotal[];
};

export function buildXlsxWorkbook(args: DownloadXlsxArgs): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(args.sheetName ?? 'Hoja1');

  const headerRow = ws.addRow(args.headers);
  headerRow.font = { bold: true };

  for (const row of args.rows) {
    ws.addRow(row.map((v) => (v == null ? '' : v)));
  }

  if (args.totals && args.totals.length > 0) {
    ws.addRow([]);
    for (const t of args.totals) {
      const r = ws.addRow([t.label, t.value]);
      r.getCell(1).font = { bold: true };
    }
  }

  ws.columns.forEach((col) => {
    let max = 10;
    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const v = cell.value == null ? '' : String(cell.value);
      if (v.length > max) max = v.length;
    });
    col.width = Math.min(max + 2, 60);
  });

  return wb;
}

/**
 * 'YYYY-MM-DD' de hoy en la hora del navegador, para el nombre del archivo.
 * `toISOString()` es el día UTC: exportando después de las 21:00 (AR) el
 * archivo salía con fecha de mañana.
 */
export function fechaArchivoHoy(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export async function downloadXlsx(args: DownloadXlsxArgs): Promise<void> {
  await downloadWorkbook(buildXlsxWorkbook(args), args.filename);
}

/**
 * Baja un workbook ya armado. Lo usan los reportes con diseño propio, que
 * construyen sus hojas a mano en vez de pasar por `buildXlsxWorkbook`.
 */
export async function downloadWorkbook(
  wb: ExcelJS.Workbook,
  filename: string,
): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
